"use client";

import { c as _c } from "react/compiler-runtime";
import React, { useCallback, useRef, useState } from "react";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { BrainView } from "./brainMobileViews";
import type { ApiWave } from "@/generated/models/ApiWave";
import MyStreamWaveTabsLeaderboard from "../my-stream/MyStreamWaveTabsLeaderboard";
import { useLayout } from "../my-stream/layout/LayoutContext";
import { useWave } from "@/hooks/useWave";
import { ArrowLeftIcon, PlusIcon } from "@heroicons/react/24/solid";
import { useUnreadIndicator } from "@/hooks/useUnreadIndicator";
import { useUnreadNotifications } from "@/hooks/useUnreadNotifications";
import { useAuth } from "@/components/auth/Auth";
import { getWaveHomeRoute } from "../../../helpers/navigation.helpers";
import { useWaveCurations } from "@/hooks/waves/useWaveCurations";
import MyStreamWaveCurationCreateDialog from "../my-stream/tabs/MyStreamWaveCurationCreateDialog";
const ACTIVE_TAB_BACKGROUND = "tw-bg-iron-800";
const INACTIVE_TAB_BACKGROUND = "tw-bg-iron-950";
const ACTIVE_TAB_TEXT = "tw-text-iron-300";
const INACTIVE_TAB_TEXT = "tw-text-iron-400";
const BASE_TAB_BUTTON_CLASS_NAME = "tw-border-none tw-no-underline tw-flex tw-justify-center tw-items-center tw-px-2 tw-py-2 tw-gap-1 tw-flex-1 tw-rounded-md";
const BASE_TAB_TEXT_CLASS_NAME = "tw-font-semibold tw-text-xs sm:tw-text-sm tw-whitespace-nowrap";
const getTabButtonClassName = (isActive: boolean): string => `${BASE_TAB_BUTTON_CLASS_NAME} ${isActive ? ACTIVE_TAB_BACKGROUND : INACTIVE_TAB_BACKGROUND}`;
const getTabTextClassName = ({
  isActive,
  additionalClasses
}: {
  readonly isActive: boolean;
  readonly additionalClasses?: string | undefined;
}): string => {
  const additionalClassName = additionalClasses ? ` ${additionalClasses}` : "";
  const textColorClassName = isActive ? ACTIVE_TAB_TEXT : INACTIVE_TAB_TEXT;
  return `${BASE_TAB_TEXT_CLASS_NAME}${additionalClassName} ${textColorClassName}`;
};
interface BrainMobileTabsProps {
  readonly activeView: BrainView;
  readonly onViewChange: (view: BrainView) => void;
  readonly wave?: ApiWave | undefined;
  readonly waveActive: boolean;
  readonly hasPolls?: boolean | undefined;
  readonly showWavesTab: boolean;
  readonly showStreamBack: boolean;
  readonly isApp?: boolean | undefined;
}
const BrainMobileTabs: React.FC<BrainMobileTabsProps> = t0 => {
  const $ = _c(126);
  const {
    activeView,
    onViewChange,
    wave,
    waveActive,
    hasPolls: t1,
    showWavesTab,
    showStreamBack,
    isApp
  } = t0;
  const hasPolls = t1 === undefined ? false : t1;
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const {
    registerRef
  } = useLayout();
  const {
    connectedProfile
  } = useAuth();
  const hasAuthenticatedProfile = Boolean(connectedProfile?.handle);
  const [isCreateCurationOpen, setIsCreateCurationOpen] = useState(false);
  const shouldShowCurationTabs = Boolean(isApp && waveActive && wave?.id);
  let t2;
  if ($[0] !== searchParams || $[1] !== shouldShowCurationTabs) {
    t2 = shouldShowCurationTabs ? searchParams.get("curation") : null;
    $[0] = searchParams;
    $[1] = shouldShowCurationTabs;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const activeCurationId = t2;
  const t3 = wave?.id ?? "";
  let t4;
  if ($[3] !== shouldShowCurationTabs || $[4] !== t3) {
    t4 = {
      waveId: t3,
      enabled: shouldShowCurationTabs
    };
    $[3] = shouldShowCurationTabs;
    $[4] = t3;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const {
    data: t5
  } = useWaveCurations(t4);
  let t6;
  if ($[6] !== t5) {
    t6 = t5 === undefined ? [] : t5;
    $[6] = t5;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  const curations = t6;
  const canManageCurations = wave?.wave.authenticated_user_eligible_for_admin === true;
  const mobileTabsRef = useRef(null);
  let t7;
  if ($[8] !== registerRef) {
    t7 = element => {
      mobileTabsRef.current = element;
      registerRef("mobileTabs", element);
    };
    $[8] = registerRef;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  const setMobileTabsRef = t7;
  const {
    isMemesWave,
    isCurationWave,
    isRankWave,
    isApproveWave
  } = useWave(wave);
  const isCompetitionWave = isRankWave || isApproveWave;
  const supportsOutcomeView = isCompetitionWave && !isCurationWave;
  const canShowMyVotesTab = isCurationWave || hasAuthenticatedProfile;
  const t8 = connectedProfile?.handle ?? null;
  let t9;
  if ($[10] !== t8) {
    t9 = {
      type: "messages",
      handle: t8
    };
    $[10] = t8;
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  const {
    hasUnread: hasUnreadMessages
  } = useUnreadIndicator(t9);
  const {
    haveUnreadNotifications
  } = useUnreadNotifications(connectedProfile?.handle ?? null);
  const scrollActiveButtonIntoView = _temp;
  let t10;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = isActive => element_1 => {
      if (isActive) {
        scrollActiveButtonIntoView(element_1);
      }
    };
    $[12] = t10;
  } else {
    t10 = $[12];
  }
  const getActiveButtonRef = t10;
  let t11;
  if ($[13] !== pathname || $[14] !== router || $[15] !== searchParams) {
    t11 = curationId => {
      const params = new URLSearchParams(searchParams.toString() || "");
      if (curationId) {
        params.set("curation", curationId);
      } else {
        params.delete("curation");
      }
      const nextQuery = params.toString();
      const nextUrl = nextQuery ? `${pathname}?${nextQuery}` : pathname;
      const currentQuery = searchParams.toString();
      const currentUrl = currentQuery ? `${pathname}?${currentQuery}` : pathname;
      if (nextUrl === currentUrl) {
        return;
      }
      router.replace(nextUrl, {
        scroll: false
      });
    };
    $[13] = pathname;
    $[14] = router;
    $[15] = searchParams;
    $[16] = t11;
  } else {
    t11 = $[16];
  }
  const updateSelectedCuration = t11;
  const isChatActive = activeView === BrainView.DEFAULT && activeCurationId === null;
  let t12;
  if ($[17] !== activeCurationId || $[18] !== curations) {
    bb0: {
      const mappedCurations = curations.map(_temp2);
      if (!activeCurationId || mappedCurations.some(curation_0 => curation_0.id === activeCurationId)) {
        t12 = mappedCurations;
        break bb0;
      }
      let t13;
      if ($[20] !== activeCurationId) {
        t13 = {
          id: activeCurationId,
          name: "Curation"
        };
        $[20] = activeCurationId;
        $[21] = t13;
      } else {
        t13 = $[21];
      }
      t12 = [t13, ...mappedCurations];
    }
    $[17] = activeCurationId;
    $[18] = curations;
    $[19] = t12;
  } else {
    t12 = $[19];
  }
  const curationTabs = t12;
  let t13;
  if ($[22] !== activeCurationId || $[23] !== isApp || $[24] !== onViewChange || $[25] !== updateSelectedCuration) {
    t13 = view => {
      const shouldPreserveSelectedCuration = isApp && view === BrainView.ABOUT && activeCurationId !== null;
      if (!shouldPreserveSelectedCuration) {
        updateSelectedCuration(null);
      }
      onViewChange(view);
    };
    $[22] = activeCurationId;
    $[23] = isApp;
    $[24] = onViewChange;
    $[25] = updateSelectedCuration;
    $[26] = t13;
  } else {
    t13 = $[26];
  }
  const handleWaveViewChange = t13;
  let t14;
  if ($[27] !== handleWaveViewChange) {
    t14 = () => {
      handleWaveViewChange(BrainView.DEFAULT);
    };
    $[27] = handleWaveViewChange;
    $[28] = t14;
  } else {
    t14 = $[28];
  }
  const onChatClick = t14;
  let t15;
  if ($[29] !== handleWaveViewChange) {
    t15 = () => {
      handleWaveViewChange(BrainView.NOTIFICATIONS);
    };
    $[29] = handleWaveViewChange;
    $[30] = t15;
  } else {
    t15 = $[30];
  }
  const onNotificationsClick = t15;
  let t16;
  if ($[31] !== onViewChange || $[32] !== updateSelectedCuration) {
    t16 = curationId_0 => {
      onViewChange(BrainView.DEFAULT);
      updateSelectedCuration(curationId_0);
    };
    $[31] = onViewChange;
    $[32] = updateSelectedCuration;
    $[33] = t16;
  } else {
    t16 = $[33];
  }
  const onCurationClick = t16;
  let t17;
  if ($[34] !== activeView || $[35] !== handleWaveViewChange || $[36] !== isCurationWave || $[37] !== wave || $[38] !== waveActive) {
    t17 = waveActive && wave && isCurationWave ? <button ref={getActiveButtonRef(activeView === BrainView.SALES)} onClick={() => handleWaveViewChange(BrainView.SALES)} className={getTabButtonClassName(activeView === BrainView.SALES)}><span className={getTabTextClassName({
        isActive: activeView === BrainView.SALES
      })}>Sales</span></button> : null;
    $[34] = activeView;
    $[35] = handleWaveViewChange;
    $[36] = isCurationWave;
    $[37] = wave;
    $[38] = waveActive;
    $[39] = t17;
  } else {
    t17 = $[39];
  }
  const salesTabButton = t17;
  let t18;
  if ($[40] !== isApp || $[41] !== onViewChange || $[42] !== router || $[43] !== showStreamBack || $[44] !== waveActive) {
    t18 = waveActive && showStreamBack && !isApp && <><button onClick={() => {
        router.push(getWaveHomeRoute({
          isDirectMessage: false,
          isApp: !!isApp
        }));
        onViewChange(BrainView.DEFAULT);
      }} className="tw-border-none tw-no-underline tw-flex tw-justify-center tw-items-center tw-px-2 tw-py-1.5  tw-gap-1 tw-flex-1 tw-rounded-md tw-bg-iron-950"><ArrowLeftIcon className="tw-size-4 tw-text-iron-400" /><span className="tw-whitespace-nowrap tw-text-xs tw-font-semibold tw-text-iron-400 sm:tw-text-sm">My Stream</span></button><div className="tw-mx-1 tw-h-4 tw-w-px tw-flex-shrink-0 tw-bg-iron-700" /></>;
    $[40] = isApp;
    $[41] = onViewChange;
    $[42] = router;
    $[43] = showStreamBack;
    $[44] = waveActive;
    $[45] = t18;
  } else {
    t18 = $[45];
  }
  let t19;
  if ($[46] !== activeView || $[47] !== handleWaveViewChange || $[48] !== showWavesTab || $[49] !== waveActive) {
    t19 = !waveActive && showWavesTab && <button ref={getActiveButtonRef(activeView === BrainView.WAVES)} onClick={() => handleWaveViewChange(BrainView.WAVES)} className={getTabButtonClassName(activeView === BrainView.WAVES)}><span className={getTabTextClassName({
        isActive: activeView === BrainView.WAVES
      })}>Waves</span></button>;
    $[46] = activeView;
    $[47] = handleWaveViewChange;
    $[48] = showWavesTab;
    $[49] = waveActive;
    $[50] = t19;
  } else {
    t19 = $[50];
  }
  let t20;
  if ($[51] !== activeView || $[52] !== handleWaveViewChange || $[53] !== hasUnreadMessages || $[54] !== isApp || $[55] !== waveActive) {
    t20 = !isApp && !waveActive && <button ref={getActiveButtonRef(activeView === BrainView.MESSAGES)} onClick={() => handleWaveViewChange(BrainView.MESSAGES)} className={getTabButtonClassName(activeView === BrainView.MESSAGES)}><span className={getTabTextClassName({
        isActive: activeView === BrainView.MESSAGES,
        additionalClasses: "tw-relative"
      })}><span>Messages</span>{hasUnreadMessages && <div className="tw-absolute -tw-right-3 tw-top-0 tw-h-2 tw-w-2 tw-rounded-full tw-bg-red" />}</span></button>;
    $[51] = activeView;
    $[52] = handleWaveViewChange;
    $[53] = hasUnreadMessages;
    $[54] = isApp;
    $[55] = waveActive;
    $[56] = t20;
  } else {
    t20 = $[56];
  }
  let t21;
  if ($[57] !== isChatActive) {
    t21 = getActiveButtonRef(isChatActive);
    $[57] = isChatActive;
    $[58] = t21;
  } else {
    t21 = $[58];
  }
  let t22;
  if ($[59] !== isChatActive) {
    t22 = getTabButtonClassName(isChatActive);
    $[59] = isChatActive;
    $[60] = t22;
  } else {
    t22 = $[60];
  }
  let t23;
  if ($[61] !== isChatActive) {
    t23 = getTabTextClassName({
      isActive: isChatActive
    });
    $[61] = isChatActive;
    $[62] = t23;
  } else {
    t23 = $[62];
  }
  const t24 = waveActive ? "Chat" : "My Stream";
  let t25;
  if ($[63] !== t23 || $[64] !== t24) {
    t25 = <span className={t23}>{t24}</span>;
    $[63] = t23;
    $[64] = t24;
    $[65] = t25;
  } else {
    t25 = $[65];
  }
  let t26;
  if ($[66] !== onChatClick || $[67] !== t21 || $[68] !== t22 || $[69] !== t25) {
    t26 = <button ref={t21} onClick={onChatClick} className={t22}>{t25}</button>;
    $[66] = onChatClick;
    $[67] = t21;
    $[68] = t22;
    $[69] = t25;
    $[70] = t26;
  } else {
    t26 = $[70];
  }
  let t27;
  if ($[71] !== activeView || $[72] !== handleWaveViewChange || $[73] !== waveActive) {
    t27 = waveActive && <button ref={getActiveButtonRef(activeView === BrainView.ABOUT)} onClick={() => handleWaveViewChange(BrainView.ABOUT)} className={getTabButtonClassName(activeView === BrainView.ABOUT)}><span className={getTabTextClassName({
        isActive: activeView === BrainView.ABOUT
      })}>About</span></button>;
    $[71] = activeView;
    $[72] = handleWaveViewChange;
    $[73] = waveActive;
    $[74] = t27;
  } else {
    t27 = $[74];
  }
  let t28;
  if ($[75] !== activeView || $[76] !== handleWaveViewChange || $[77] !== hasPolls || $[78] !== wave || $[79] !== waveActive) {
    t28 = waveActive && wave && hasPolls && <button ref={getActiveButtonRef(activeView === BrainView.POLLS)} onClick={() => handleWaveViewChange(BrainView.POLLS)} className={getTabButtonClassName(activeView === BrainView.POLLS)}><span className={getTabTextClassName({
        isActive: activeView === BrainView.POLLS
      })}>Polls</span></button>;
    $[75] = activeView;
    $[76] = handleWaveViewChange;
    $[77] = hasPolls;
    $[78] = wave;
    $[79] = waveActive;
    $[80] = t28;
  } else {
    t28 = $[80];
  }
  const t29 = !isCompetitionWave && salesTabButton;
  let t30;
  if ($[81] !== activeView || $[82] !== canShowMyVotesTab || $[83] !== handleWaveViewChange || $[84] !== isCompetitionWave || $[85] !== isMemesWave || $[86] !== salesTabButton || $[87] !== supportsOutcomeView || $[88] !== wave || $[89] !== waveActive) {
    t30 = waveActive && wave && isCompetitionWave && <><MyStreamWaveTabsLeaderboard wave={wave} activeView={activeView} onViewChange={handleWaveViewChange} renderAfterLeaderboard={salesTabButton} />{canShowMyVotesTab && <><button ref={getActiveButtonRef(activeView === BrainView.MY_VOTES)} onClick={() => handleWaveViewChange(BrainView.MY_VOTES)} className={getTabButtonClassName(activeView === BrainView.MY_VOTES)}><span className={getTabTextClassName({
            isActive: activeView === BrainView.MY_VOTES
          })}>My Votes</span></button></>}{supportsOutcomeView && <button ref={getActiveButtonRef(activeView === BrainView.OUTCOME)} onClick={() => handleWaveViewChange(BrainView.OUTCOME)} className={getTabButtonClassName(activeView === BrainView.OUTCOME)}><span className={getTabTextClassName({
          isActive: activeView === BrainView.OUTCOME
        })}>Outcome</span></button>}{isMemesWave && <button ref={getActiveButtonRef(activeView === BrainView.FAQ)} onClick={() => handleWaveViewChange(BrainView.FAQ)} className={getTabButtonClassName(activeView === BrainView.FAQ)}><span className={getTabTextClassName({
          isActive: activeView === BrainView.FAQ
        })}>FAQ</span></button>}</>;
    $[81] = activeView;
    $[82] = canShowMyVotesTab;
    $[83] = handleWaveViewChange;
    $[84] = isCompetitionWave;
    $[85] = isMemesWave;
    $[86] = salesTabButton;
    $[87] = supportsOutcomeView;
    $[88] = wave;
    $[89] = waveActive;
    $[90] = t30;
  } else {
    t30 = $[90];
  }
  let t31;
  if ($[91] !== activeCurationId || $[92] !== activeView || $[93] !== curationTabs || $[94] !== onCurationClick || $[95] !== shouldShowCurationTabs) {
    t31 = shouldShowCurationTabs && curationTabs.map(curation_1 => {
      const isActive_0 = activeView === BrainView.DEFAULT && curation_1.id === activeCurationId;
      return <button key={curation_1.id} type="button" data-curation-id={curation_1.id} ref={getActiveButtonRef(isActive_0)} onClick={() => onCurationClick(curation_1.id)} className={getTabButtonClassName(isActive_0)}><span className={getTabTextClassName({
          isActive: isActive_0,
          additionalClasses: "tw-max-w-28 tw-truncate sm:tw-max-w-36"
        })}>{curation_1.name}</span></button>;
    });
    $[91] = activeCurationId;
    $[92] = activeView;
    $[93] = curationTabs;
    $[94] = onCurationClick;
    $[95] = shouldShowCurationTabs;
    $[96] = t31;
  } else {
    t31 = $[96];
  }
  let t32;
  if ($[97] !== canManageCurations || $[98] !== isApp) {
    t32 = isApp && canManageCurations && <button type="button" onClick={() => setIsCreateCurationOpen(true)} className="tw-inline-flex tw-h-9 tw-w-9 tw-flex-shrink-0 tw-items-center tw-justify-center tw-rounded-xl tw-border tw-border-solid tw-border-iron-700 tw-bg-iron-900 tw-text-iron-200 tw-transition desktop-hover:hover:tw-border-iron-500 desktop-hover:hover:tw-bg-iron-800 desktop-hover:hover:tw-text-white" aria-label="Create curation"><PlusIcon className="tw-size-4 tw-flex-shrink-0" /></button>;
    $[97] = canManageCurations;
    $[98] = isApp;
    $[99] = t32;
  } else {
    t32 = $[99];
  }
  let t33;
  if ($[100] !== activeView || $[101] !== haveUnreadNotifications || $[102] !== isApp || $[103] !== onNotificationsClick || $[104] !== waveActive) {
    t33 = !isApp && !waveActive && <button ref={getActiveButtonRef(activeView === BrainView.NOTIFICATIONS)} onClick={onNotificationsClick} className={getTabButtonClassName(activeView === BrainView.NOTIFICATIONS)}><span className={getTabTextClassName({
        isActive: activeView === BrainView.NOTIFICATIONS,
        additionalClasses: "tw-relative"
      })}>Notifications{haveUnreadNotifications && <div className="tw-absolute -tw-right-1 -tw-top-1 tw-h-2 tw-w-2 tw-rounded-full tw-bg-red" />}</span></button>;
    $[100] = activeView;
    $[101] = haveUnreadNotifications;
    $[102] = isApp;
    $[103] = onNotificationsClick;
    $[104] = waveActive;
    $[105] = t33;
  } else {
    t33 = $[105];
  }
  let t34;
  if ($[106] !== t18 || $[107] !== t19 || $[108] !== t20 || $[109] !== t26 || $[110] !== t27 || $[111] !== t28 || $[112] !== t29 || $[113] !== t30 || $[114] !== t31 || $[115] !== t32 || $[116] !== t33) {
    t34 = <div className="tw-flex tw-w-full tw-items-center tw-justify-start tw-gap-1 tw-overflow-x-auto tw-overflow-y-hidden tw-rounded-lg tw-border tw-border-solid tw-border-iron-800 tw-bg-iron-950 tw-p-1 tw-py-1 tw-scrollbar-thin tw-scrollbar-track-iron-800 tw-scrollbar-thumb-iron-500 desktop-hover:hover:tw-scrollbar-thumb-iron-300">{t18}{t19}{t20}{t26}{t27}{t28}{t29}{t30}{t31}{t32}{t33}</div>;
    $[106] = t18;
    $[107] = t19;
    $[108] = t20;
    $[109] = t26;
    $[110] = t27;
    $[111] = t28;
    $[112] = t29;
    $[113] = t30;
    $[114] = t31;
    $[115] = t32;
    $[116] = t33;
    $[117] = t34;
  } else {
    t34 = $[117];
  }
  let t35;
  if ($[118] !== isCreateCurationOpen || $[119] !== onCurationClick || $[120] !== wave) {
    t35 = wave && isCreateCurationOpen && <MyStreamWaveCurationCreateDialog wave={wave} isOpen={isCreateCurationOpen} onClose={() => setIsCreateCurationOpen(false)} onSaved={curation_2 => {
      onCurationClick(curation_2.id);
      setIsCreateCurationOpen(false);
    }} />;
    $[118] = isCreateCurationOpen;
    $[119] = onCurationClick;
    $[120] = wave;
    $[121] = t35;
  } else {
    t35 = $[121];
  }
  let t36;
  if ($[122] !== setMobileTabsRef || $[123] !== t34 || $[124] !== t35) {
    t36 = <div ref={setMobileTabsRef} className="tw-overflow-x-auto tw-px-2 tw-py-2 sm:tw-px-4 md:tw-px-6">{t34}{t35}</div>;
    $[122] = setMobileTabsRef;
    $[123] = t34;
    $[124] = t35;
    $[125] = t36;
  } else {
    t36 = $[125];
  }
  return t36;
};
export default BrainMobileTabs;
function _temp(element_0) {
  element_0?.scrollIntoView({
    behavior: "smooth",
    inline: "center",
    block: "nearest"
  });
}
function _temp2(curation) {
  return {
    id: curation.id,
    name: curation.name
  };
}